import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import DevBanner from './components/DevBanner';
import TrackingScripts from './components/TrackingScripts';
import { WebsiteContentProvider } from './contexts/WebsiteContentContext';
import ScrollToTop from './components/ScrollToTop';
import { trackAttributionFromUrl } from './utils/attribution';
import Header from './components/Header';
import Footer from './components/Footer';
import PageLayout from './components/layout/PageLayout';
import WhatsAppLeadCapture from './components/WhatsAppLeadCapture';
import ScrollCue from './components/shared/ScrollCue';
/** /home → / keeping ?gclid / utm params and #hash for ad attribution. */
function HomeAlias() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/', search, hash }} replace />;
}
// Lazy-loaded pages — only downloaded when the user navigates to them
const HomePage = lazy(() => import('./pages/HomePage'));
const PropertyPage = lazy(() => import('./pages/PropertyPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const CalculatorPage = lazy(() => import('./pages/tools/CalculatorPage'));
const AffordabilityPage = lazy(() => import('./pages/tools/AffordabilityPage'));
const TaxSavingsPage = lazy(() => import('./pages/tools/TaxSavingsPage'));
const CreditCheckPage = lazy(() => import('./pages/tools/CreditCheckPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const CareersQuestionnairePage = lazy(() => import('./pages/CareersQuestionnairePage'));
const CareersDocumentsPage = lazy(() => import('./pages/CareersDocumentsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ConsultationPage = lazy(() => import('./pages/ConsultationPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const HomeLoansPage = lazy(() => import('./pages/services/HomeLoansPage'));
const ResalesPage = lazy(() => import('./pages/services/ResalesPage'));
const RentalsPage = lazy(() => import('./pages/services/RentalsPage'));
const TrusteePage = lazy(() => import('./pages/services/TrusteePage'));
const InsurancePage = lazy(() => import('./pages/services/InsurancePage'));
const TrustCreationPage = lazy(() => import('./pages/services/TrustCreationPage'));
const CompanyRegistrationPage = lazy(() => import('./pages/services/CompanyRegistrationPage'));
const WillsPage = lazy(() => import('./pages/services/WillsPage'));
const MobileAppPage = lazy(() => import('./pages/MobileAppPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const CapitalInvestmentPage = lazy(() => import('./pages/services/CapitalInvestmentPage'));
const MillionaireStrategyPage = lazy(() => import('./pages/MillionaireStrategyPage'));
const EducationPage = lazy(() => import('./pages/EducationPage'));
const FacebookCompetitionRulesPage = lazy(() => import('./pages/FacebookCompetitionRulesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ReviewSubmissionPage = lazy(() => import('./pages/ReviewSubmissionPage'));
const InvestLandingPage = lazy(() => import('./pages/InvestLandingPage'));
const FamilyPtyPage = lazy(() => import('./pages/FamilyPtyPage'));
const FamilyPtyCalculatorPage = lazy(() => import('./pages/FamilyPtyCalculatorPage'));
const FamilyPtyApplyPage = lazy(() => import('./pages/FamilyPtyApplyPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));

/**
 * Campaign landing pages (/invest/:token) render their own slim chrome —
 * the site Header/Footer and the WhatsApp bubble would give ad traffic
 * places to wander off to (Jaco 2026-09-16). /family-pty left this list at
 * its public launch (2026-10-12) and now has the normal site chrome.
 */
function ChromeVisible({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (/^\/invest\/.+/.test(pathname)) return null;
  return <>{children}</>;
}

/** The floating WhatsApp button: hidden on the applicant's questionnaire + documents link pages (Jaco 2026-10-22). */
function WhatsAppButtonVisible({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (/^\/careers\/(questionnaire|documents)\//.test(pathname)) return null;
  return <>{children}</>;
}

export default function App() {
  useEffect(() => {
    trackAttributionFromUrl();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <WebsiteContentProvider>
      <TrackingScripts />
      <DevBanner />
      <Toaster position="top-center" />
      <Router>
        <ScrollToTop />
        <div className="flex flex-col min-h-screen bg-white">
          <ChromeVisible><Header /></ChromeVisible>
          <main className="flex-grow">
            <Suspense fallback={<div className="flex-grow" />}>
            <Routes>
              <Route element={<PageLayout />}>
                <Route path="/" element={<HomePage />} />
                {/* Pages retired 2026-09 (hosting also 301s these — firebase.json):
                    About Us folded into the home page, Beginners Guide replaced
                    by the Education video page. */}
                {/* Old Google Ads still link /home (hosting 301s it too — firebase.json) */}
                <Route path="/home" element={<HomeAlias />} />
                <Route path="/about-us" element={<Navigate to="/" replace />} />
                <Route path="/beginners" element={<Navigate to="/education" replace />} />
                <Route path="/tools" element={<Navigate to="/tools/calculator" replace />} />
                <Route path="/tools/calculator" element={<CalculatorPage />} />
                <Route path="/tools/affordability" element={<AffordabilityPage />} />
                <Route path="/tools/tax-savings" element={<TaxSavingsPage />} />
                <Route path="/tools/credit-check" element={<CreditCheckPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/best-property-investment-opportunities" element={<PropertyPage />} />
                <Route path="/contactus" element={<ContactPage />} />
                <Route path="/consultation" element={<ConsultationPage />} />
                <Route path="/home-loans" element={<HomeLoansPage />} />
                <Route path="/re-finance" element={<Navigate to="/home-loans" replace />} />
                <Route path="/resales" element={<ResalesPage />} />
                <Route path="/rentals" element={<RentalsPage />} />
                <Route path="/independent-trustee" element={<TrusteePage />} />
                <Route path="/insurance" element={<InsurancePage />} />
                <Route path="/trust-creation" element={<TrustCreationPage />} />
                <Route path="/company-registration" element={<CompanyRegistrationPage />} />
                <Route path="/wills" element={<WillsPage />} />
                <Route path="/capital-investment" element={<CapitalInvestmentPage />} />
                {/* Unlisted campaign lander (like /services) — not in nav/footer */}
                <Route path="/millionaire-strategy" element={<MillionaireStrategyPage />} />
                {/* Education video page — replaces About Us in the nav (phase B) */}
                <Route path="/education" element={<EducationPage />} />
                {/* /whatsapp legacy URL — channel discontinued, redirect to contact page */}
                <Route path="/whatsapp" element={<Navigate to="/contactus" replace />} />
              </Route>
              {/* Careers: no hero slider / qualify band — the page opens on its intro, then the application form (Jaco 2026-10-19) */}
              <Route path="/careers" element={<CareersPage />} />
              {/* Junior Consultant questionnaire — personal 48-hour link from email/WhatsApp (unlisted) */}
              <Route path="/careers/questionnaire/:token" element={<CareersQuestionnairePage />} />
              {/* "Upload your CV + photo" — personal 14-day link from email (unlisted, 2026-10-27) */}
              <Route path="/careers/documents/:token" element={<CareersDocumentsPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/facebookcompetitionrules" element={<FacebookCompetitionRulesPage />} />
              <Route path="/mobile-app" element={<MobileAppPage />} />
              {/* Account deletion instructions — linked from the Play Store Data Safety form */}
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              {/* Redirect slug for WhatsApp template buttons — multi-development campaigns */}
              <Route path="/property/all-investments" element={<Navigate to="/best-property-investment-opportunities" replace />} />
              <Route path="/property/:slugOrId" element={<PropertyDetailPage />} />
              {/* Public review-submission landing — opened from a WhatsApp follow-up button */}
              <Route path="/review/:token" element={<ReviewSubmissionPage />} />
              {/* Unlisted per-development campaign landers — token generated in the admin CMS */}
              <Route path="/invest/:token" element={<InvestLandingPage />} />
              {/* Group Millionaire Strategy (Family Pty) page — public since the
                  2026-10-12 launch (Website CMS → Family Pty) */}
              <Route path="/family-pty" element={<FamilyPtyPage />} />
              {/* Its calculator and registration form on their own URLs —
                  still noindex and Disallowed (robots: /family-pty/). */}
              <Route path="/family-pty/calculator" element={<FamilyPtyCalculatorPage />} />
              <Route path="/family-pty/apply" element={<FamilyPtyApplyPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </main>
          <ChromeVisible><Footer /></ChromeVisible>
        </div>
        <ChromeVisible><WhatsAppButtonVisible><WhatsAppLeadCapture /></WhatsAppButtonVisible></ChromeVisible>
        <ScrollCue />
      </Router>
      </WebsiteContentProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
